/**
 * Static placeholder data with the content of the Figma frames. Used by the WP1.1 screens
 * until the real endpoints/listeners land (WP1.3/1.4/1.6) and by the component gallery.
 */
import type {
  HeroDTO,
  HomeRowDTO,
  Member,
  PlaylistDTO,
  PublicUserDTO,
  QueueNode,
  SessionDTO,
  SessionMeta,
  SongDTO,
  ToneDTO,
} from '@akpl/shared';
import { COLORS } from '@akpl/ui-tv';

/** Valid ULIDs (Crockford base32, first char 0-7). */
export function pid(n: number): string {
  return `01J8Z5K3M4N5P6Q7R8S9T0S${String(n).padStart(3, '0')}`;
}

export const tones: ToneDTO[] = [
  { name: '-1', label: { en: '-1 semitone', es: '-1 semitono' }, semitones: -1, isDefault: false },
  { name: '0', label: { en: 'Original', es: 'Original' }, semitones: 0, isDefault: true },
  { name: '+1', label: { en: '+1 semitone', es: '+1 semitono' }, semitones: 1, isDefault: false },
];

function song(
  n: number,
  title: string,
  artist: string,
  extra: Partial<SongDTO> & { color?: string } = {},
): SongDTO & { color: string } {
  const { color, ...rest } = extra;
  return {
    id: pid(n),
    title,
    artists: [{ id: pid(100 + n), name: artist }],
    imageUrl: null,
    genre: null,
    tier: 'standard',
    tones,
    durationMs: 200_000 + n * 7_000,
    hasLyrics: true,
    locked: false,
    color: color ?? COLORS.blue,
    ...rest,
  };
}

export const songs = [
  song(1, 'Bohemian Rhapsody', 'Queen', { color: COLORS.green, durationMs: 355_000 }),
  song(2, 'Flowers', 'Miley Cyrus', { color: COLORS.cyan, tier: 'recent', durationMs: 200_000 }),
  song(3, 'Under Pressure', 'Queen', {
    color: COLORS.teal,
    tier: 'vip',
    locked: true,
    durationMs: 244_000,
  }),
  song(4, 'Vivir mi vida', 'Marc Anthony', { color: COLORS.orange, durationMs: 225_000 }),
  song(5, 'Tusa', 'Karol G', { color: COLORS.purple, durationMs: 200_000 }),
  song(6, 'Hawái', 'Maluma', { color: COLORS.amber, durationMs: 199_000 }),
  song(7, "Don't Stop Me Now", 'Queen', { color: COLORS.cyan, durationMs: 209_000 }),
  song(8, 'We Will Rock You', 'Queen', { color: COLORS.blue, durationMs: 122_000 }),
  song(9, 'Somebody to Love', 'Queen', { color: COLORS.orange, durationMs: 296_000 }),
  song(10, 'Qué bonito', 'Rosario', { color: COLORS.purple, durationMs: 221_000 }),
  song(11, 'Querida', 'Juan Gabriel', { color: COLORS.amber, durationMs: 263_000 }),
  song(12, 'Qué será', 'José Feliciano', {
    color: COLORS.pink,
    tier: 'recent',
    durationMs: 192_000,
  }),
  song(13, 'Despacito', 'Luis Fonsi', { color: COLORS.pink, durationMs: 228_000 }),
  song(14, 'La Bicicleta', 'Shakira', { color: COLORS.teal, durationMs: 228_000 }),
  song(15, 'Livin’ on a Prayer', 'Bon Jovi', { color: COLORS.blue, durationMs: 249_000 }),
  song(16, 'De música ligera', 'Soda Stereo', { color: COLORS.green, durationMs: 213_000 }),
];

function playlist(
  n: number,
  en: string,
  es: string,
  total: number,
  color: string,
  description: string | null = null,
) {
  const p: PlaylistDTO & { color: string } = {
    id: pid(200 + n),
    kind: 'editorial',
    name: { en, es },
    description,
    imageUrl: null,
    artistId: null,
    total,
    color,
  };
  return p;
}

export const playlists = [
  playlist(
    1,
    'Rock classics',
    'Clásicos del rock',
    24,
    COLORS.blue,
    'Queen, Soda Stereo, Bon Jovi',
  ),
  playlist(2, 'Latin party', 'Fiesta latina', 32, COLORS.orange),
  playlist(3, 'Ballads to cry to', 'Baladas para llorar', 18, COLORS.purple),
  playlist(4, 'English pop', 'Pop en inglés', 40, COLORS.cyan),
  playlist(5, 'Cumbia & salsa', 'Cumbia y salsa', 27, COLORS.pink),
  playlist(6, '80s hits', 'Éxitos de los 80', 35, COLORS.green),
  playlist(7, 'Reggaetón', 'Reggaetón', 50, COLORS.amber),
  playlist(8, 'Duets', 'Duetos', 15, COLORS.teal),
];

export const heroes: HeroDTO[] = [
  {
    id: pid(300),
    name: 'Clásicos del rock',
    type: 'playlist',
    refId: playlists[0]!.id,
    posterTvUrl: '',
    posterMobileUrl: '',
    playlist: playlists[0]!,
  },
  {
    id: pid(301),
    name: 'Vivir mi vida',
    type: 'song',
    refId: songs[3]!.id,
    posterTvUrl: '',
    posterMobileUrl: '',
    song: songs[3]!,
  },
  {
    id: pid(302),
    name: 'Fiesta latina',
    type: 'playlist',
    refId: playlists[1]!.id,
    posterTvUrl: '',
    posterMobileUrl: '',
    playlist: playlists[1]!,
  },
];

export const homeRows: HomeRowDTO[] = [
  { id: pid(400), type: 'heroes', title: { en: 'Featured', es: 'Destacados' }, items: heroes },
  { id: pid(401), type: 'playlists', title: { en: 'Playlists', es: 'Listas' }, items: playlists },
  { id: pid(402), type: 'songs', title: { en: 'Songs', es: 'Canciones' }, items: songs },
];

export const hostUser: PublicUserDTO = {
  id: 'dev-host-uid',
  displayName: 'Tomas Rivera',
  photoUrl: null,
  isAnonymous: false,
};

export const devSession: SessionDTO = {
  id: pid(500),
  joinCode: 'MQ7Z2K',
  hostUid: hostUser.id,
  hostName: 'Tomas Rivera',
  deviceId: 'dev-device',
  status: 'active',
  startedAt: '2026-09-07T20:15:00.000Z',
  expiresAt: '2026-09-08T02:15:00.000Z',
  closedAt: null,
  closedReason: null,
  features: ['catalog.standard', 'catalog.recent', 'catalog.vip', 'tone.change', 'queue.unlimited'],
};

export const devMeta: SessionMeta = {
  joinCode: devSession.joinCode,
  hostUid: hostUser.id,
  hostName: devSession.hostName,
  status: 'active',
  startedAt: Date.parse(devSession.startedAt),
  expiresAt: Date.parse(devSession.expiresAt),
  features: devSession.features,
  version: 1,
};

export const devMembers: Record<string, Member> = {
  [hostUser.id]: { name: 'Tomas', role: 'host', joinedAt: devMeta.startedAt },
  'dev-guest-1': { name: 'Lucía', role: 'guest', joinedAt: devMeta.startedAt + 60_000 },
  'dev-guest-2': { name: 'Diego', role: 'guest', joinedAt: devMeta.startedAt + 120_000 },
};

const t0 = devMeta.startedAt;
function qi(
  n: number,
  s: (typeof songs)[number],
  tone: string,
  by: { uid: string; name: string },
  status: 'queued' | 'playing' | 'played',
) {
  return {
    songId: s.id,
    title: s.title,
    artistNames: s.artists.map((a) => a.name).join(', '),
    imageUrl: null,
    tone,
    toneLabel: { en: `Key ${tone}`, es: `Tono ${tone}` },
    addedBy: by,
    addedVia: 'mobile' as const,
    order: n * 1000,
    status,
    addedAt: t0 + n * 30_000,
    ...(status !== 'queued' ? { startedAt: t0 + n * 60_000 } : {}),
    ...(status === 'played' ? { playedAt: t0 + n * 60_000 + 200_000 } : {}),
  };
}

const tomas = { uid: hostUser.id, name: 'Tomas' };
const lucia = { uid: 'dev-guest-1', name: 'Lucía' };
const diego = { uid: 'dev-guest-2', name: 'Diego' };

export const devQueue: QueueNode = {
  [pid(601)]: qi(1, songs[12]!, '0', lucia, 'played'),
  [pid(602)]: qi(2, songs[13]!, '0', diego, 'played'),
  [pid(603)]: qi(3, songs[3]!, '+1', tomas, 'playing'),
  [pid(604)]: qi(4, songs[0]!, '0', lucia, 'queued'),
  [pid(605)]: qi(5, songs[4]!, '-1', diego, 'queued'),
  [pid(606)]: qi(6, songs[5]!, '+2', tomas, 'queued'),
};

/** Flat cover color for a placeholder song/playlist id (real data uses `imageUrl`). */
export function colorOf(id: string): string | undefined {
  return (
    songs.find((s) => s.id === id)?.color ?? playlists.find((p) => p.id === id)?.color ?? undefined
  );
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '';
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
